import React from "react";
import { Button, Badge, Tooltip, Space, Typography, Alert, Spin } from "antd";
import {
  CheckCircleOutlined,
  WarningOutlined,
  DisconnectOutlined,
  LinkOutlined,
} from "@ant-design/icons";
import { useQuery } from "@tanstack/react-query";
import { getQbStatus, getQbAuthUrl } from "../../api/quickbooks";
import type { QbStatus } from "../../types/dashboard";

const { Text } = Typography;

/** Days before refresh token expiry that we consider "expiring soon". */
const EXPIRY_WARNING_DAYS = 14;

function daysUntil(isoDate: string | null): number | null {
  if (!isoDate) return null;
  const diff = new Date(isoDate).getTime() - Date.now();
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
}

function formatDateFr(isoDate: string | null): string {
  if (!isoDate) return "—";
  return new Intl.DateTimeFormat("fr-CA", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(isoDate));
}

/**
 * Displays QuickBooks connection status and provides an OAuth connect button.
 * Shows a warning badge when the refresh token is expiring within 14 days.
 */
export default function ConnectButton(): React.JSX.Element {
  const {
    data: status,
    isLoading,
    error,
  } = useQuery<QbStatus, Error>({
    queryKey: ["qbStatus"],
    queryFn: getQbStatus,
    staleTime: 60 * 1000,
    retry: 1,
  });

  async function handleConnect(): Promise<void> {
    const url = await getQbAuthUrl();
    window.location.href = url;
  }

  if (isLoading) {
    return <Spin size="small" />;
  }

  if (error) {
    return (
      <Alert
        type="error"
        message="Impossible de vérifier le statut QuickBooks."
        showIcon
      />
    );
  }

  if (!status) return <></>;

  if (!status.connected) {
    return (
      <Button
        type="primary"
        icon={<LinkOutlined />}
        onClick={() => void handleConnect()}
      >
        Connecter QuickBooks
      </Button>
    );
  }

  const refreshDays = daysUntil(status.refreshTokenExpiry);
  const isExpiringSoon =
    refreshDays !== null && refreshDays <= EXPIRY_WARNING_DAYS;

  if (isExpiringSoon) {
    return (
      <Space direction="vertical" size={4}>
        <Tooltip
          title={`Le jeton de rafraîchissement expire dans ${refreshDays} jour(s). Reconnectez-vous pour éviter une interruption.`}
        >
          <Badge status="warning" text={
            <Space>
              <WarningOutlined style={{ color: "#faad14" }} />
              <Text type="warning">Reconnexion requise</Text>
            </Space>
          } />
        </Tooltip>
        <Button
          size="small"
          icon={<LinkOutlined />}
          onClick={() => void handleConnect()}
        >
          Reconnecter QuickBooks
        </Button>
      </Space>
    );
  }

  return (
    <Space direction="vertical" size={4}>
      <Badge
        status="success"
        text={
          <Space>
            <CheckCircleOutlined style={{ color: "#52c41a" }} />
            <Text>Connecté à QuickBooks</Text>
          </Space>
        }
      />
      {status.realmId && (
        <Text type="secondary" style={{ fontSize: 12 }}>
          Realm ID: {status.realmId}
        </Text>
      )}
      {status.lastRefresh && (
        <Text type="secondary" style={{ fontSize: 12 }}>
          Dernier rafraîchissement: {formatDateFr(status.lastRefresh)}
        </Text>
      )}
    </Space>
  );
}

/**
 * Compact inline version: icon + badge only, used in the app header.
 */
export function QbStatusBadge(): React.JSX.Element {
  const { data: status, isLoading } = useQuery<QbStatus, Error>({
    queryKey: ["qbStatus"],
    queryFn: getQbStatus,
    staleTime: 60 * 1000,
    retry: 1,
  });

  if (isLoading) return <Spin size="small" />;
  if (!status) return <DisconnectOutlined style={{ color: "#ff4d4f" }} />;

  const refreshDays = daysUntil(status.refreshTokenExpiry);
  const isExpiringSoon =
    refreshDays !== null && refreshDays <= EXPIRY_WARNING_DAYS;

  if (!status.connected) {
    return (
      <Tooltip title="QuickBooks non connecté">
        <DisconnectOutlined style={{ color: "#ff4d4f" }} />
      </Tooltip>
    );
  }

  if (isExpiringSoon) {
    return (
      <Tooltip title={`Reconnexion requise — expire dans ${refreshDays} jour(s)`}>
        <WarningOutlined style={{ color: "#faad14" }} />
      </Tooltip>
    );
  }

  return (
    <Tooltip title="QuickBooks connecté">
      <CheckCircleOutlined style={{ color: "#52c41a" }} />
    </Tooltip>
  );
}
